import { useMemo, useState, useSyncExternalStore } from 'react';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import { assetsHighlightReelGenerateCreate, type HighlightReelGenerateContentTypeEnum } from '@goldcast/api/content';
import Button from '@/components/atoms/Button/Button';
import IconButton from '@/components/atoms/Button/IconButton';
import Icon from '@/components/atoms/Icon';
import AspectRatioTiles from '@/components/molecules/AspectRatioTiles/AspectRatioTiles';
import { DEFAULT_ASPECT_RATIO, type AspectRatioValue } from '@/components/molecules/AspectRatioTiles/constants';
import { classnames } from '@/libs/utils';
import { showErrorToast, showSuccessToast } from '@/libs/toast/toast';
import { useAppContext } from '@/context/AppContext/AppContext';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';

const TARGET_TIME_RANGES = [30, 60, 90] as const;
const DEFAULT_TARGET_TIME_RANGE = 60;

// The backend's HighlightReelGenerate.content_type accepts exactly these two choices.
const CONTENT_TYPE_OPTIONS: { value: HighlightReelGenerateContentTypeEnum; label: string }[] = [
  { value: 'keynote', label: 'Keynote' },
  { value: 'product-highlights', label: 'Product highlights' }
];
// Matches the backend default for HighlightReelGenerate.content_type.
const DEFAULT_CONTENT_TYPE: HighlightReelGenerateContentTypeEnum = 'keynote';

/**
 * One option in a segmented control, shared by the content type and target time range groups.
 *
 * Selected pills carry the design's white fill, 1px gray-200 border and default two-layer shadow;
 * unselected pills keep a transparent border of the same width so selecting one doesn't shift the
 * row by 2px.
 */
export function SegmentedButton({
  label,
  isSelected,
  testId,
  onClick
}: {
  label: string;
  isSelected: boolean;
  testId: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      data-testid={testId}
      onClick={onClick}
      className={classnames(
        'flex-1 rounded-md border py-1.5 text-xs font-medium transition-all duration-150 ease-in-out',
        isSelected
          ? 'border-gray-200 bg-white text-slate-900 shadow'
          : 'border-transparent text-slate-500 hover:text-slate-700'
      )}
    >
      {label}
    </button>
  );
}

export default function GenerateHighlightReelPanel({
  onCancel,
  onGenerated
}: {
  onCancel: () => void;
  onGenerated: () => void;
}) {
  const { eventId, broadcastId } = useParams<{ eventId: string; broadcastId: string }>();
  const { logger } = useAppContext();
  const [duration, setDuration] = useState<number>(DEFAULT_TARGET_TIME_RANGE);
  const [aspectRatio, setAspectRatio] = useState<AspectRatioValue>(DEFAULT_ASPECT_RATIO);
  const [contentType, setContentType] = useState<HighlightReelGenerateContentTypeEnum>(DEFAULT_CONTENT_TYPE);

  // Render state, so it has to re-run when the flag lands — only the subscription guarantees that.
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const isContentTypeEnabled = useMemo(
    () => !!featureFlags[FeatureFlagKeys.Use_CL_Content_Type_For_HLR],
    [featureFlags]
  );

  const { mutate: generateReel, isLoading: isGenerating } = useMutation(
    () =>
      assetsHighlightReelGenerateCreate({
        body: {
          content_id: broadcastId as string,
          project_id: eventId as string,
          highlight_reel_duration: duration,
          aspect_ratio: aspectRatio,
          ...(isContentTypeEnabled ? { content_type: contentType } : {})
        }
      }),
    {
      onSuccess: () => {
        showSuccessToast('Highlight reel generation has started. We’ll let you know when it’s ready.');
        onGenerated();
      },
      onError: (err: any) => {
        showErrorToast('Could not start highlight reel generation. Please try again.');
        logger.error('Failed to generate highlight reel', err);
      }
    }
  );

  return (
    <div className="flex flex-col">
      <div className="flex h-14 w-full items-center justify-between p-4 px-5">
        <span className="text-lg font-medium">Generate Highlight Reels</span>
        <IconButton
          icon="IconX"
          title="Close"
          variation="text"
          trackingId="generate-highlight-reel-close-button"
          onClick={onCancel}
        />
      </div>

      <div className="px-5 pb-5">
        <AspectRatioTiles
          label="Target platform"
          tileGapClassName="gap-2"
          value={aspectRatio}
          onChange={setAspectRatio}
        />
        {isContentTypeEnabled && (
          <>
            <div className="mt-4 text-xs font-medium text-slate-500">Content type</div>
            <div
              role="radiogroup"
              aria-label="Content type"
              className="mt-2 flex w-full items-center gap-1 rounded-lg bg-slate-100 p-1"
            >
              {CONTENT_TYPE_OPTIONS.map(option => (
                <SegmentedButton
                  key={option.value}
                  label={option.label}
                  isSelected={contentType === option.value}
                  testId={`highlight-reel-content-type-${option.value}`}
                  onClick={() => setContentType(option.value)}
                />
              ))}
            </div>
          </>
        )}
        <div className="mt-4 text-xs font-medium text-slate-500">Target time range</div>
        <div
          role="radiogroup"
          aria-label="Target time range"
          className="mt-2 flex w-full items-center gap-1 rounded-lg bg-slate-100 p-1"
        >
          {TARGET_TIME_RANGES.map(range => (
            <SegmentedButton
              key={range}
              label={`${range} s`}
              isSelected={duration === range}
              testId={`highlight-reel-duration-${range}`}
              onClick={() => setDuration(range)}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t px-5 py-3">
        <Button variation="text" trackingId="generate-highlight-reel-cancel-button" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variation="filled"
          trackingId="generate-highlight-reel-submit-button"
          disabled={isGenerating}
          onClick={() => generateReel()}
        >
          <Icon
            name={isGenerating ? 'IconLoader2' : 'IconSparkles'}
            size={14}
            className={classnames('mr-1.5', { 'animate-spin': isGenerating })}
          />
          Generate
        </Button>
      </div>
    </div>
  );
}
